/* Copyright (c)2023 Sky Co., LTD. All rights reserved. */
/*
 * 
 * 【注意】基本的に追加・変更禁止。削除はOK。
 * 
 * このファイルは Android Enterprise API v1 のフォーマットに沿って作成したものです。
 * そのため、SKYSEA独自のメンバーを追加したり、既存のメンバーの名前変更は行わないでください。
 * 使っていないものの削除はOKです。
 *
 * */
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants'

/**
 * デフォルトで値を保護するように設定されたセキュリティ ポリシー。 
 * デバイスのセキュリティ体制を維持するために、既定値を上書きすることはお勧めしません。
 */
export class AMAPI_AdvancedSecurityOverrides {
    /**
     * Controls Common Criteria Mode — 情報技術セキュリティ評価のコモン クライテリアで定義されたセキュリティ標準 (https://www.commoncriteriaportal.org/) (CC). 
     */
    commonCriteriaMode?: string | null;
    /**
     * 開発者設定へのアクセスを制御します
     */
    developerSettings?: string | null;
    /**
     * Google Play プロテクトの検証を適用するかどうか。 ensureVerifyAppsEnabled (非推奨) を置き換えます。(https://support.google.com/accounts/answer/2812853)
     */
    googlePlayProtectVerifyApps?: string | null;
    /**
     * NotificationListenerService を使用して仕事用プロファイルの通知を読み取ることができる個人用アプリ (https://developer.android.com/reference/android/service/notification/NotificationListenerService). 
     * デフォルトでは、個人用アプリ (システム アプリを除く) は仕事の通知を読み取ることができません。 
     * リスト内の各値はパッケージ名でなければなりません。
     */
    personalAppsThatCanReadWorkNotifications?: string[] | null;
    /**
     * デバイスに適用される、信頼されていないアプリ (提供元不明のアプリ) のポリシー。 install_unknown_sources_allowed (非推奨) を置き換えます。
     */
    untrustedAppsPolicy?: string | null;
}
/**
 * 常時接続の VPN 接続の構成。
 */
export class AMAPI_AlwaysOnVpnPackage {
    /**
     * VPN が接続されていない場合、ネットワークを許可しません。
     */
    lockdownEnabled?: boolean | null;
    /**
     * VPN アプリのパッケージ名。
     */
    packageName?: string | null;
}
/**
 * デバイスの Android Framework API レベルが最小要件を満たしていない場合に満たされるコンプライアンス ルール条件。
 * ポリシーごとに、このタイプの条件を持つルールは 1 つだけ存在できます。
 */
export class AMAPI_ApiLevelCondition {
    /**
     * 最低限必要な Android フレームワーク API レベル。
     * デバイスが最小要件を満たしていない場合、この条件は満たされています。 ゼロより大きい必要があります。
     */
    minApiLevel?: number | null;
}
/**
 * アプリに関する情報。
 */
export class AMAPI_Application {
    /**
     * このアプリが無料か、アプリ内購入で無料か、有料か。
     * 価格が指定されていない場合、これはアプリが一般提供されなくなったことを意味します (アプリを所有しているユーザーは引き続き利用できる可能性があります)。
     */
    appPricing?: string | null;
    /**
     * 企業に表示されるアプリケーション トラック。
     */
    appTracks?: AMAPI_AppTrackInfo[];
    /**
     * このアプリで現在利用可能なバージョン。
     */
    appVersions?: AMAPI_AppVersion[];
    /**
     * アプリの作成者の名前 (アプリ開発者など)。
     */
    author?: string | null;
    /**
     * ISO 3166-1 alpha-2 に従ってこのアプリを利用できる国。
     */
    availableCountries?: string[] | null;
    /**
     * アプリのカテゴリ (ex. RACING, SOCIAL, etc.)
     */
    category?: string | null;
    /**
     * このアプリのコンテンツ レーティング。
     */
    contentRating?: string | null;
    /**
     * ローカライズされたプロモーションの説明 (ある場合)。
     */
    description?: string | null;
    /**
     * パッケージを誰にどのように提供するか。
     */
    distributionChannel?: string | null;
    /**
     * このアプリの注目すべき機能 (ある場合)。
     */
    features?: string[] | null;
    /**
     * 利用可能な場合は、完全なアプリの説明。
     */
    fullDescription?: string | null;
    /**
     * アプリのアイコンとして使用できる画像へのリンク。 この画像は、512 x 512 のピクセル サイズまでの使用に適しています。
     */
    iconUrl?: string | null;
    /**
     * アプリ用に事前構成できる一連の管理プロパティ。
     */
    managedProperties?: AMAPI_ManagedProperty[];
    /**
     * アプリを実行するために最低限必要な Android SDK。
     */
    minAndroidSdkVersion?: number | null;
    /**
     * アプリの名前 : enterprises/{enterprise\}/applications/{package_name\}
     */
    name?: string | null;
    /**
     * アプリに必要なアクセス許可。
     */
    permissions?: AMAPI_ApplicationPermission[];
    /**
     * アプリの (コンシューマー) Google Play 詳細ページへのリンク。
     */
    playStoreUrl?: string | null;
    /**
     * アプリに加えられた最近の変更のローカライズされた説明。
     */
    recentChanges?: string | null;
    /**
     * アプリを表すスクリーンショット リンクのリスト。
     */
    screenshotUrls?: string[] | null;
    /**
     * アプリのアイコンとして使用できる小さな画像へのリンク。
     * この画像は、128 x 128 のピクセル サイズまでの使用に適しています。
     */
    smallIconUrl?: string | null;
    /**
     * アプリのタイトル。 ローカライズ。
     */
    title?: string | null;
    /**
     * 出力のみ。アプリが最後に公開されたおおよその時間 (7 日以内)。
     */
    updateTime?: string | null;
}
/**
 * アプリ関連のイベント。
 */
export class AMAPI_ApplicationEvent {
    /**
     * イベントの作成時間。
     */
    createTime?: string | null;
    /**
     * アプリのイベントの種類。
     */
    eventType?: string | null;
}
/**
 * アプリに必要な許可。
 */
export class AMAPI_ApplicationPermission {
    /**
     * パーミッションの詳細な説明であり、その影響の詳細を提供します。 ローカライズ。
     */
    description?: string | null;
    /**
     * 権限の名前。 ローカライズ。
     */
    name?: string | null;
    /**
     * 権限を一意に識別する不透明な文字列。  ローカライズではない
     */
    permissionId?: string | null;
}
/**
 * 個々のアプリのポリシー。
 */
export class AMAPI_ApplicationPolicy {
    /**
     * 企業に属するデバイスがアクセスできるアプリのトラック ID のリスト。
     * リストに複数のトラック ID が含まれている場合、デバイスはアクセス可能なすべてのトラックの最新バージョンを受け取ります。
     * リストにトラック ID が含まれていない場合、デバイスはアプリのプロダクション トラックにのみアクセスできます。
     * 各トラックの詳細については、AppTrackInfo を参照してください。
     */
    accessibleTrackIds?: string[] | null;
    /**
     * VPN が接続されておらず、alwaysOnVpnPackage でない場合に、アプリがネットワークを許可するかどうかを指定します。
     * lockdownEnabled が有効になっています。
     * VPN_LOCKDOWN_ENFORCED に設定すると、アプリのネットワークは許可されません。VPN_LOCKDOWN_EXEMPTION に設定すると、アプリのネットワークは許可されます。
     * Android 10 以降を実行しているデバイスでのみサポートされます。
     * これがデバイスでサポートされていない場合、デバイスには、non_compliance_reason が API_LEVEL に設定された NonComplianceDetail と fieldPath が含まれます。
     * これがアプリに適用されない場合、デバイスには、non_compliance_reason が UNSUPPORTED に設定された NonComplianceDetail と fieldPath が含まれます。
     * fieldPath は applications[i].alwaysOnVpnLockdownExemption に設定されます。ここで、i はアプリケーション ポリシー内のパッケージのインデックスです。
     */
    alwaysOnVpnLockdownExemption?: string | null;
    /**
     * アプリの自動更新モードを制御します。
     */
    autoUpdateMode?: string | null;
    /**
     * ユーザーの同意を条件として、アプリがデバイスの仕事用プロファイルと個人用プロファイルを介してアプリ自体と通信できるかどうかを制御します。
     */
    connectedWorkAndPersonalApp?: string | null;
    /**
     * アプリが要求するすべてのアクセス許可の既定のポリシー。
     * 指定すると、すべてのアプリに適用されるポリシー レベルの default_permission_policy が上書きされます。
     * すべてのアプリに適用される permission_grants をオーバーライドするわけではありません。
     */
    defaultPermissionPolicy?: string | null;
    /**
     * Android Device Policy からアプリに委任されたスコープ。
     */
    delegatedScopes?: string[] | null;
    /**
     * アプリが無効になっているかどうか。 無効にしても、アプリ データは引き続き保持されます。
     */
    disabled?: boolean | null;
    /**
     * このアプリを拡張アプリとして有効にし、Android デバイス ポリシーをオフラインで操作できるようにするための構成。
     * このフィールドは、最大 1 つのアプリに対して設定できます。
     */
    extensionConfig?: AMAPI_ExtensionConfig;
    /**
     * 実行するインストールのタイプ。
     */
    installType?: string | null;
    /**
     * アプリがフルスクリーン モードで自身をロックできるかどうか。
     * 非推奨。
     * 専用デバイスを構成するには、InstallType KIOSK または kioskCustomLauncherEnabled を使用します。
     */
    lockTaskAllowed?: boolean | null;
    /**
     * アプリに適用される管理対象の構成。 
     * 構成の形式は、アプリでサポートされている ManagedProperty の値によって決定されます。
     * 管理構成の各フィールド名は、ManagedProperty のキー フィールドと一致する必要があります。
     * フィールド値は ManagedProperty のタイプと互換性がある必要があります: *type* *JSON value* BOOL true or false STRING string INTEGER number CHOICE string MULTISELECT array of strings HIDDEN string BUNDLE_ARRAY array of objects
     */
    managedConfiguration?: {
        [key: string]: any;
    } | null;
    /**
     * 管理構成 iframe から保存された、アプリの管理構成テンプレート。
     * managed_configuration が設定されている場合、このフィールドは無視されます。
     */
    managedConfigurationTemplate?: AMAPI_ManagedConfigurationTemplate;
    /**
     * デバイスで実行されるアプリの最小バージョン。
     * 設定されている場合、デバイスは少なくともこのバージョン コードにアプリを更新しようとします。
     * アプリが最新でない場合、デバイスには、non_compliance_reason が APP_NOT_UPDATED に設定された NonComplianceDetail が含まれます。
     * アプリは、この値以上のバージョン コードで Google Play に公開されている必要があります。
     * ポリシーごとに最小バージョン コードを指定できるのは、最大 20 個のアプリです。
     */
    minimumVersionCode?: number | null;
    /**
     * アプリのパッケージ名。 ex, com.google.android.youtube
     */
    packageName?: string | null;
    /**
     * アプリに対する明示的なアクセス許可の付与または拒否。
     * これらの値は、すべてのアプリに適用される default_permission_policy と permission_grants をオーバーライドします。
     */
    permissionGrants?: AMAPI_PermissionGrant[];
    /**
     * Specifies whether the app installed in the work profile is allowed to add widgets to the home screen.
     */
    workProfileWidgets?: string | null;
}

export class AMAPI_ApplicationPolicyExtend extends AMAPI_ApplicationPolicy{
    // 手入力で追加したもののアプリ名
    appNameforScv?: string | null;
}

/**
 * インストール済みアプリについて報告された情報。
 */
export class AMAPI_ApplicationReport {
    /**
     * パッケージのソース。
     */
    applicationSource?: string | null;
    /**
     * アプリの表示名。
     */
    displayName?: string | null;
    /**
     * 過去 30 時間に発生したアプリ イベントのリスト。
     */
    events?: AMAPI_ApplicationEvent[];
    /**
     * このアプリをインストールしたアプリのパッケージ名。
     */
    installerPackageName?: string | null;
    /**
     * アプリによって報告されたキー付きアプリの状態のリスト。
     */
    keyedAppStates?: AMAPI_KeyedAppState[];
    /**
     * アプリのパッケージ名。
     */
    packageName?: string | null;
    /**
     * アプリの APK ファイルの SHA-256 ハッシュ。アプリが変更されていないことを確認するために使用できます。
     * ハッシュ値の各バイトは、2 桁の 16 進数で表されます。
     */
    packageSha256Hash?: string | null;
    /**
     * アプリ パッケージに関連付けられた各 android.content.pm.Signature の SHA-1 ハッシュ。
     * 各ハッシュ値の各バイトは、2 桁の 16 進数で表されます。
     * (https://developer.android.com/reference/android/content/pm/Signature.html)
     */
    signingKeyCertFingerprints?: string[] | null;
    /**
     * アプリケーションの状態。
     */
    state?: string | null;
    /**
     * アプリのバージョン コード。あるバージョンが別のバージョンよりも新しいかどうかを判断するために使用できます。
     */
    versionCode?: number | null;
    /**
     * ユーザーに表示されるアプリのバージョン。
     */
    versionName?: string | null;
}
/**
 * アプリケーション レポートの動作を制御する設定。
 */
export class AMAPI_ApplicationReportingSettings {
    /**
     * 削除されたアプリをアプリケーション レポートに含めるかどうか。
     */
    includeRemovedApps?: boolean | null;
}
/**
 * プロセスに関する情報。 これには、プロセス名、開始時刻、アプリ Uid、アプリ Pid、seinfo タグ、ベース APK のハッシュが含まれます。
 */
export class AMAPI_AppProcessInfo {
    /**
     * 基本 APK の SHA-256 ハッシュ (16 進形式)。
     */
    apkSha256Hash?: string | null;
    /**
     * 特定のユーザー ID に関連付けられているすべてのパッケージのパッケージ名。
     * ほとんどの場合、これは単一のパッケージ名 (そのユーザー ID が割り当てられたパッケージ) になります。
     * 複数のアプリケーションが UID を共有する場合、UID を共有するすべてのパッケージが含まれます。
     */
    packageNames?: string[] | null;
    /**
     * プロセス ID。
     */
    pid?: number | null;
    /**
     * プロセス名。
     */
    processName?: string | null;
    /**
     * SELinux ポリシー情報。
     */
    seinfo?: string | null;
    /**
     * プロセス開始時刻。
     */
    startTime?: string | null;
    /**
     * パッケージの UID。
     */
    uid?: number | null;
}
/**
 * アプリ トラックの関連付けに名前を付けるための ID。
 */
export class AMAPI_AppTrackInfo {
    /**
     * Play Console で設定された trackId に関連付けられたトラック名。
     * 名前は Play Console から変更できます。
     */
    trackAlias?: string | null;
    /**
     * アプリのトラック情報を表示する Play Console ページの URL の releaseTrackId から取得された、変更不可能な一意のトラック識別子。
     */
    trackId?: string | null;
}
/**
 * これは、アプリの 1 つのバージョンを表します。
 */
export class AMAPI_AppVersion {
    /**
     * 値が True の場合、このバージョンがプロダクション トラックであることを示します。
     */
    production?: boolean | null;
    /**
     * アプリのバージョンが公開されている識別子を追跡します。
     * これにはプロダクション トラックは含まれません (代わりにプロダクションを参照してください)。
     */
    trackIds?: string[] | null;
    /**
     * アプリ バージョンの一意の増加する識別子。
     */
    versionCode?: number | null;
    /**
     * バージョンを識別するためにアプリ デベロッパーが Play ストアで使用する文字列。文字列は必ずしも一意またはローカライズされているとは限りません (ex, "1.4").
     */
    versionString?: string | null;
}
/**
 * 完全に管理されたデバイスまたは仕事用プロファイルでアプリとデータへのアクセスをブロックするアクション。
 * このアクションは、コンプライアンスの問題を修正する方法に関する情報 (可能な場合) を含むユーザー向けの通知を表示するために、デバイスまたは仕事用プロファイルもトリガーします。
 * 注:wipeAction も指定する必要があります。
 */
export class AMAPI_BlockAction {
    /**
     * デバイスまたは仕事用プロファイルがブロックされるまでの、ポリシーが準拠していない日数。
     * すぐにアクセスをブロックするには、0 に設定します。
     * blockAfterDays は、wipeAfterDays より小さくなければなりません。
     */
    blockAfterDays?: number | null;
    /**
     * この BlockAction のスコープを指定します。
     * 会社所有のデバイスにのみ適用されます。
     */
    blockScope?: string | null;
}
/**
 * 秘密鍵へのアプリのアクセスを制御します。
 * ルールは、指定されたアプリに Android デバイス ポリシーが付与する秘密鍵 (存在する場合) を決定します。
 * アプリが KeyChain.choosePrivateKeyAlias (または任意のオーバーロード) を呼び出して特定の URL の秘密キー エイリアスを要求した場合、または URL 固有ではないルール (つまり、urlPattern が設定されていないか、空の文字列または .*) Android 11 以降では、アプリが KeyChain を直接呼び出せるようにします。
 */
export class AMAPI_ChoosePrivateKeyRule {
    /**
     * このルールが適用されるパッケージ名。 各アプリの署名証明書のハッシュは、Play が提供するハッシュに対して検証されます。 
     * パッケージ名が指定されていない場合、KeyChain.choosePrivateKeyAlias またはオーバーロードを呼び出すすべてのアプリにエイリアスが提供されます。 
     * (Android 11以降であっても、KeyChain.choosePrivateKeyAliasを呼び出さないわけではありません。). 
     * ここで指定されたパッケージと同じ Android UID を持つアプリは、KeyChain.choosePrivateKeyAlias を呼び出すとアクセスできます。
     * (https://developer.android.com/reference/android/security/KeyChain#choosePrivateKeyAlias%28android.app.Activity,%20android.security.KeyChainAliasCallback,%20java.lang.String[],%20java.security.Principal[],%20java.lang.String,%20int,%20java.lang.String%29) 
     */
    packageNames?: string[] | null;
    /**
     * 使用する秘密鍵のエイリアス。
     */
    privateKeyAlias?: string | null;
    /**
     * リクエストの URL と照合する URL パターン。設定されていないか空の場合、すべての URL に一致します。 これは、java.util.regex.Pattern の正規表現構文を使用します。
     */
    urlPattern?: string | null;
}
/**
 * 指定したアプリのデータをデバイスから消去するための CLEAR_APP_DATA コマンドに関連付けられたパラメーター。
 */
export class AMAPI_ClearAppsDataParams {
    /**
     * コマンドの実行時にデータが消去されるアプリのパッケージ名。
     */
    packageNames?: string[] | null;
}
/**
 * 指定したアプリのデータをデバイスから消去する CLEAR_APP_DATA コマンドのステータス。
 */
//export class AMAPI_ClearAppsDataStatus {
//    /**
//     * アプリごとの結果、パッケージ名からそれぞれのクリア結果へのマッピング。
//     */
//    results?: {
//        [key: string]: AMAPI_PerAppResult;
//    } | null;
//}
/**
 * Common Criteria Mode に関する情報 — Common Criteria for Information Technology Security Evaluation で定義されたセキュリティ標準。
 * この情報は、デバイスのポリシーで statusReportingSettings.commonCriteriaModeEnabled が true である場合にのみ利用できます。
 * (https://www.commoncriteriaportal.org/)
 */
export class AMAPI_CommonCriteriaModeInfo {
    /**
     * コモン クライテリア モードが有効かどうか。
     */
    commonCriteriaModeStatus?: string | null;
}
/**
 * デバイスがポリシーに準拠していない場合に実行する緩和アクションを宣言するルール。
 * すべてのルールには、デバイス リソースの policy_compliant を false に設定し、デバイスがポリシーに準拠していないことを示すメッセージをデバイスに表示する暗黙的な緩和アクションが常に存在します。 
 * ルールのフィールド値に応じて、その他の緩和アクションもオプションで実行できます。
 */
export class AMAPI_ComplianceRule {
    /**
     * デバイスの Android Framework API レベルが最小要件を満たしていない場合に満たされる条件。
     */
    apiLevelCondition?: AMAPI_ApiLevelCondition;
    /**
     * true に設定すると、アプリを無効にする緩和アクションがルールに含まれるため、デバイスは実質的に無効になりますが、アプリ データは保持されます。
     * デバイスがロックされたタスク モードでアプリを実行している場合、アプリは閉じられ、非準拠の理由を示す UI が表示されます。
     */
    disableApps?: boolean | null;
    /**
     * デバイスに一致する NonComplianceDetail が存在する場合に満たされる条件。
     */
    nonComplianceDetailCondition?: AMAPI_NonComplianceDetailCondition;
    /**
     * 設定されている場合、ルールにはリストで指定されたアプリを無効にする緩和アクションが含まれますが、アプリ データは保持されます。
     */
    packageNamesToDisable?: string[] | null;
}
/**
 * デバイスに適用されるクロスプロファイル ポリシー。
 */
export class AMAPI_CrossProfilePolicies {
    /**
     * 1 つのプロファイル (個人用または仕事用) からコピーしたテキストを他のプロファイルに貼り付けることができるかどうか。
     */
    crossProfileCopyPaste?: string | null;
    /**
     * 1 つのプロファイル (個人用または仕事用) のデータを他のプロファイルのアプリと共有できるかどうか。
     * 具体的には、インテントを介して単純なデータ共有を制御します。
     * 連絡先検索、コピー/貼り付け、または接続された仕事用アプリと個人用アプリなど、他のプロファイル間通信チャネルの管理は、個別に構成されます。
     */
    crossProfileDataSharing?: string | null;
    /**
     * 仕事用プロファイルに保存されている連絡先を、個人用プロファイルの連絡先検索と着信通話に表示できるかどうか。
     */
    showWorkContactsInPersonalProfile?: string | null;
    /**
     * 仕事用プロファイル ウィジェットのデフォルトの動作を指定します。
     * ポリシーで特定のアプリケーションの work_profile_widgets が指定されていない場合、ここで指定された値に従って動作します。
     */
    workProfileWidgetsDefault?: string | null;
}
/**
 * 誕生日など、カレンダーの日付全体または一部を表します。
 * 時刻とタイムゾーンは、別の場所で指定されているか、重要ではありません。
 * 日付はグレゴリオ暦を基準にしています。
 * これは、次のいずれかを表すことができます。: ゼロ以外の年、月、日の価値を持つ完全な日付。ゼロ年を含む月と日 (記念日など)。0 月と 0 日を含む 1 年。ゼロ日を含む年と月 (ex, クレジットカードの有効期限).
 * 関連する種類: google.type.TimeOfDay google.type.DateTime google.protobuf.Timestamp
 */
export class AMAPI_Date {
    /**
     * 日。1 から 31 の範囲で、年と月に有効である必要があります。または、年を単独で指定するか、日が重要でない年と月を指定するには 0 を指定する必要があります。
     */
    day?: number | null;
    /**
     * 月。月と日なしで年を指定するには、1 から 12、または 0 でなければなりません。
     */
    month?: number | null;
    /**
     * 年。1 から 9999 まで、または年なしで日付を指定するには 0 でなければなりません。
     */
    year?: number | null;
}
/**
 * Android Device Policy をオフラインで操作する機能を備えた拡張アプリとしてアプリを有効にするための構成。
 * Android バージョン 13 以降の場合、拡張アプリはバッテリー制限から除外されるため、制限されたアプリ スタンバイ バケットには配置されません。(https://developer.android.com/topic/performance/appstandby#restricted-bucket)
 * 拡張機能アプリは、ユーザーがデータを消去したり、アプリケーションを強制終了したりすることからも保護されますが、Android 13 以降で必要に応じて、管理者は拡張機能アプリでアプリ データの消去コマンドを引き続き使用できます。(https://developer.android.com/management/reference/rest/v1/enterprises.devices/issueCommand#CommandType)
 */
export class AMAPI_ExtensionConfig {
    /**
     * Androidデバイスポリシー用のレシーバーサービスクラスの完全に適格なクラス名拡張アプリにローカルコマンドステータスの更新を通知します。
     */
    notificationReceiver?: string | null;
    /**
     * 拡張アプリの署名証明書の 16 進数でエンコードされた SHA-256 ハッシュ。
     * 64 文字の 16 進文字列表現のみが有効です。指定されていない場合は、対応するパッケージ名の署名が代わりに Play ストアから取得されます。
     * このリストが空の場合、アプリが Android デバイス ポリシーと通信できるようにするには、デバイス上の拡張アプリの署名が Play ストアから取得した署名と一致する必要があります。
     * このリストが空でない場合、デバイス上の拡張アプリの署名は、Androidデバイスポリシーと通信できるように、このリストのエントリの1つと一致する必要があります。
     * 実稼働のユース ケースでは、これを空のままにしておくことをお勧めします。
     */
    signingKeyFingerprintsSha256?: string[] | null;
}
/**
 * システム凍結期間。
 * デバイスのクロックが凍結期間内にある場合、受信するすべてのシステム アップデート (セキュリティ パッチを含む) がブロックされ、インストールされません。
 * デバイスが凍結期間外の場合、通常の更新動作が適用されます。
 * *閏年は凍結期間の計算では無視されます。特に: * 2 月 29 日が凍結期間の開始日または終了日として設定されている場合、凍結期間は代わりに 2 月 28 日に開始または終了します。 
 * *デバイスのシステム クロックが 2 月 29 日の場合、2 月 28 日として扱われます。
 * *凍結期間の日数または 2 つの凍結期間の間の時間を計算する場合、2 月 29 日は無視され、1 日としてカウントされません。
 * 注: フリーズ期間を有効にするには、SystemUpdateType を SYSTEM_UPDATE_TYPE_UNSPECIFIED として指定することはできません。これは、フリーズ期間には定義済みのポリシーを指定する必要があるためです。
 */
export class AMAPI_FreezePeriod {
    /**
     * 凍結期間の終了日 (含む)。開始日から 90 日以内である必要があります。
     * 終了日が開始日より前の場合、凍結期間は年末を締めくくると見なされます。
     * 注: 年は設定しないでください. ex, {"month": 1,"date": 30\}.
     */
    endDate?: AMAPI_Date;
    /**
     * 凍結期間の開始日 (含む)。注: 年は設定しないでください。 ex, {"month": 1,"date": 30\}.
     */
    startDate?: AMAPI_Date;
}
/**
 * アプリによって報告されたキー付きアプリ状態。
 */
export class AMAPI_KeyedAppState {
    /**
     * デバイスでのアプリ状態の作成時刻。
     */
    createTime?: string | null;
    /**
     * オプションで、EMM によって読み取られる機械可読値。
     * 例: 管理者が EMM コンソールで照会するために選択できる値を設定する (ex. “notify me if the battery_warning data < 10”).
     */
    data?: string | null;
    /**
     * アプリの状態のキー。アプリが状態を提供しているものの参照ポイントとして機能します。
     * 例: 管理された構成のフィードバックを提供する場合、このキーは管理された構成キーである可能性があります。
     */
    key?: string | null;
    /**
     * アプリの状態が最後に更新された時刻。
     */
    lastUpdateTime?: string | null;
    /**
     * 必要に応じて、アプリの状態を説明する自由形式のメッセージ文字列。
     * 状態が特定の値によってトリガーされた場合(ex. 管理された構成値), メッセージに含める必要があります。
     */
    message?: string | null;
    /**
     * アプリの状態の重大度。
     */
    severity?: string | null;
}
/**
 * キオスク モードでのデバイスの動作を制御する設定。
 * キオスク モードを有効にするには、kioskCustomLauncherEnabled を true に設定するか、ポリシーで installType KIOSK を使用してアプリを指定します。
 */
export class AMAPI_KioskCustomization {
    /**
     * キオスク モードで設定アプリを許可するかどうかを指定します。
     */
    deviceSettings?: string | null;
    /**
     * ユーザーが電源ボタンを長押ししたときのキオスク モードでのデバイスの動作を設定します。
     */
    powerButtonActions?: string | null;
    /**
     * キオスク モードでシステム情報と通知を無効にするかどうかを指定します。
     */
    statusBar?: string | null;
    /**
     * クラッシュしたアプリや応答しないアプリのシステム エラー ダイアログをキオスク モードでブロックするかどうかを指定します。
     * ブロックされると、ユーザーが UI で「アプリを閉じる」オプションを選択したかのように、システムはアプリを強制停止します。
     */
    systemErrorWarnings?: string | null;
    /**
     * キオスク モードで有効にするナビゲーション機能 (ホーム、概要ボタンなど) を指定します。
     */
    systemNavigation?: string | null;
}
/**
 * アプリを起動するアクション。
 */
export class AMAPI_LaunchAppAction {
    /**
     * 起動するアプリのパッケージ名
     */
    packageName?: string | null;
}
/**
 * 管理構成 iframe から保存された、アプリの管理構成テンプレート。
 */
export class AMAPI_ManagedConfigurationTemplate {
    /**
     * オプションで、構成用に定義された構成変数を含むマップ。
     */
    configurationVariables?: {
        [key: string]: string;
    } | null;
    /**
     * 管理対象構成テンプレートの ID。
     */
    templateId?: string | null;
}
/**
 * 管理プロパティ
 */
export class AMAPI_ManagedProperty {
    /**
     * プロパティのデフォルト値。BUNDLE_ARRAY プロパティにはデフォルト値がありません。
     */
    defaultValue?: any | null;
    /**
     * プロパティの詳細な説明で、影響の詳細を提供します。ローカライズ。
     */
    description?: string | null;
    /**
     * CHOICE または MULTISELECT プロパティの場合、可能なエントリのリスト。
     */
    entries?: AMAPI_ManagedPropertyEntry[];
    /**
     * アプリがプロパティを識別するために使用する一意のキー, ex. "com.google.android.gm.fieldname".
     */
    key?: string | null;
    /**
     * BUNDLE_ARRAY プロパティの場合、ネストされたプロパティのリスト。BUNDLE_ARRAY プロパティの深さは最大で 2 レベルです。
     */
    nestedProperties?: AMAPI_ManagedProperty[];
    /**
     * プロパティの名前。ローカライズ。
     */
    title?: string | null;
    /**
     * プロパティのタイプ。
     */
    type?: string | null;
}
/**
 * 管理プロパティのエントリ。
 */
export class AMAPI_ManagedPropertyEntry {
    /**
     * 人間が判読できる値の名前。ローカライズ。
     */
    name?: string | null;
    /**
     * エントリのマシン読み取り可能な値。構成で使用する必要があります。ローカライズされていません。
     */
    value?: string | null;
}
/**
 * デバイスに一致する NonComplianceDetail が存在する場合に満たされるコンプライアンス ルール条件。
 * NonComplianceDetailCondition 内で設定されたすべてのフィールドが対応する NonComplianceDetail フィールドと一致する場合、NonComplianceDetail は NonComplianceDetailCondition と一致します。
 */
export class AMAPI_NonComplianceDetailCondition {
    /**
     * デバイスが設定に準拠していない理由。設定されていない場合、この条件は任意の理由に一致します。
     */
    nonComplianceReason?: string | null;
    /**
     * 準拠していないアプリのパッケージ名。設定されていない場合、この条件は任意のパッケージ名と一致します。
     */
    packageName?: string | null;
    /**
     * ポリシー設定の名前。これは、最上位のポリシー フィールドの JSON フィールド名です。設定されていない場合、この条件は任意の設定名と一致します。
     */
    settingName?: string | null;
}
/**
 * Wi-Fi 構成に関連する非準拠の追加コンテキスト。
 */
export class AMAPI_OncWifiContext {
    /**
     * 準拠していない Wi-Fi 構成の GUID。
     */
    wifiGuid?: string | null;
}
/**
 * パッケージ名のリスト。
 */
export class AMAPI_PackageNameList {
    /**
     * パッケージ名のリスト。
     */
    packageNames?: string[] | null;
}
/**
 * パスワード ポリシーに関連するコンプライアンス違反の追加のコンテキスト。
 */
export class AMAPI_PasswordPoliciesContext {
    /**
     * 非準拠パスワードの範囲。
     */
    passwordPolicyScope?: string | null;
}
/**
 * Requirements for the password used to unlock a device.
 */
export class AMAPI_PasswordRequirements {
    /**
     * デバイスがワイプされる前に入力できる、誤ったデバイス ロック解除パスワードの数。値 0 は、制限がないことを意味します。
     */
    maximumFailedPasswordsForWipe?: number | null;
    /**
     * パスワードの有効期限切れ。
     */
    passwordExpirationTimeout?: string | null;
    /**
     * パスワード履歴の長さ。
     * このフィールドを設定すると、ユーザーは履歴内のパスワードと同じ新しいパスワードを入力できなくなります。
     * 値 0 は、制限がないことを意味します。
     */
    passwordHistoryLength?: number | null;
    /**
     * パスワードの最小許容長。
     * 値 0 は、制限がないことを意味します。password_quality が NUMERIC, NUMERIC_COMPLEX, ALPHABETIC, ALPHANUMERIC, or COMPLEX. の場合にのみ適用されます
     */
    passwordMinimumLength?: number | null;
    /**
     * パスワードに必要な最小文字数。 password_quality が  COMPLEX.の場合にのみ適用されます
     */
    passwordMinimumLetters?: number | null;
    /**
     * パスワードに必要な小文字の最小数。 password_quality が COMPLEX.の場合にのみ適用されます
     */
    passwordMinimumLowerCase?: number | null;
    /**
     * パスワードに必要な非文字 (数字または記号) の最小数。 password_quality が COMPLEX.の場合にのみ適用されます.
     */
    passwordMinimumNonLetter?: number | null;
    /**
     * パスワードに必要な数字の最小桁数。 password_quality が COMPLEX.の場合にのみ適用されます.
     */
    passwordMinimumNumeric?: number | null;
    /**
     * パスワードに必要な記号の最小数。 password_quality が COMPLEX.の場合にのみ適用されます.
     */
    passwordMinimumSymbols?: number | null;
    /**
     * パスワードに必要な大文字の最小数。 password_quality が COMPLEX.の場合にのみ適用されます.
     */
    passwordMinimumUpperCase?: number | null;
    /**
     * 必要なパスワードの品質。
     */
    passwordQuality?: string | null;
    /**
     * パスワード要件が適用される範囲。
     */
    passwordScope?: string | null;
    /**
     * 強力な形式の認証 (パスワード、PIN、パターン) を使用してデバイスまたは仕事用プロファイルのロックを解除した後、他の認証方法を使用してロックを解除できる時間の長さ (ex. fingerprint, trust agents, face). 
     * 指定された期間が経過すると、強力な形式の認証のみを使用して、デバイスまたは仕事用プロファイルのロックを解除できます。
     */
    requirePasswordUnlock?: string | null;
    /**
     * 仕事用プロファイルで Android 9 以降を実行しているデバイスで、デバイスと仕事用プロファイルに対して統合ロックを許可するかどうかを制御します。 
     * これは、password_scope が SCOPE_PROFILE に設定されている場合にのみ設定できます。それ以外の場合、ポリシーは拒否されます。
     * ユーザーが個別のワーク ロックを設定しておらず、このフィールドが REQUIRE_SEPARATE_WORK_LOCK に設定されている場合、nonComplianceReason が USER_ACTION に設定された NonComplianceDetail が報告されます。
     */
    unifiedLockSettings?: string | null;
}
/**
 * Android パーミッションとその付与状態の構成。
 */
export class AMAPI_PermissionGrant {
    /**
     * Android の権限またはグループ。android.permission.READ_CALENDAR または android.permission_group.CALENDAR。
     */
    permission?: string | null;
    /**
     * アクセス許可を付与するためのポリシー。
     */
    policy?: string | null;
}
/**
 * 特定のインテント フィルタに一致するインテントを処理するためのデフォルト アクティビティ。
 * 注: キオスクをセットアップするには、永続的な優先アクティビティを使用するのではなく、InstallType to KIOSK を使用します。
 */
export class AMAPI_PersistentPreferredActivity {
    /**
     * フィルターで照合するインテント アクション。フィルターに何らかのアクションが含まれている場合、インテントのアクションは、一致する値の 1 つである必要があります。
     * アクションが含まれていない場合、インテント アクションは無視されます。
     */
    actions?: string[] | null;
    /**
     * フィルターで照合するインテント カテゴリ。インテントには必要なカテゴリが含まれており、一致するためにはそのすべてがフィルタに含まれている必要があります。
     * つまり、フィルタにカテゴリを追加しても、そのカテゴリがインテントで指定されていない限り、一致には影響しません。
     */
    categories?: string[] | null;
    /**
     * デフォルトのインテント ハンドラーにする必要があるアクティビティ。 これは Android コンポーネント名である必要があります。
     * ex. com.android.enterprise.app/.MainActivity. 
     * または、値がアプリのパッケージ名である場合もあります。これにより、Android Device Policy はアプリから適切なアクティビティを選択してインテントを処理します。
     */
    receiverActivity?: string | null;
}
/**
 * 仕事用プロファイルを持つ会社所有デバイスの個人用プロファイル内のアプリのポリシー。
 */
export class AMAPI_PersonalApplicationPolicy {
    /**
     * 実行するインストールのタイプ。
     */
    installType?: string | null;
    /**
     * アプリケーションのパッケージ名。
     */
    packageName?: string | null;
}
/**
 * 仕事用プロファイルを使用して、会社所有のデバイスでの個人的な使用を制御するポリシー。
 */
export class AMAPI_PersonalUsagePolicies {
    /**
     * ユーザーが管理できないアカウントの種類。
     */
    accountTypesWithManagementDisabled?: string[] | null;
    /**
     * true の場合、個人プロファイルでカメラが無効になります。
     */
    cameraDisabled?: boolean | null;
    /**
     * 仕事用プロファイルをオフにしておく時間を制御します。期間は少なくとも 3 日間である必要があります。
     */
    maxDaysWithWorkOff?: number | null;
    /**
     * 個人プロファイルのアプリケーションに適用されるポリシー。
     */
    personalApplications?: AMAPI_PersonalApplicationPolicy[];
    /**
     * personalApplications と一緒に使用して、個人プロファイル内のアプリを許可またはブロックする方法を制御します。
     */
    personalPlayStoreMode?: string | null;
    /**
     * true の場合、すべてのユーザーに対してスクリーン キャプチャが無効になります。
     */
    screenCaptureDisabled?: boolean | null;
}
/**
 * ポリシー リソースは、管理対象デバイスとそれにインストールされているアプリの動作を管理する設定のグループを表します。
 */
export class AMAPI_Policy {
    /**
     * ユーザーが管理できないアカウントの種類。
     */
    accountTypesWithManagementDisabled?: string[] | null;
    /**
     * 新しいユーザーとプロファイルの追加を無効にするかどうか。
     */
    addUserDisabled?: boolean | null;
    /**
     * マスター音量の調整を無効にするかどうか。また、デバイスをミュートします。
     */
    adjustVolumeDisabled?: boolean | null;
    /**
     * デフォルトで値を保護するように設定されたセキュリティ ポリシー。 
     * デバイスのセキュリティ体制を維持するために、既定値を上書きすることはお勧めしません。
     */
    advancedSecurityOverrides?: AMAPI_AdvancedSecurityOverrides;
    /**
     * 常時接続の VPN 接続の構成。 この設定が変更されないようにするには、vpn_config_disabled とともに使用します。
     */
    alwaysOnVpnPackage?: AMAPI_AlwaysOnVpnPackage;
    /**
     * アプリは、デバイスがアクセスできる Android デバイス ポリシーを追跡します。 
     * デバイスは、アクセス可能なすべてのトラックから最新バージョンを受け取ります。
     * トラックが指定されていない場合、デバイスはプロダクション トラックのみを使用します。
     */
    androidDevicePolicyTracks?: string[] | null;
    /**
     * 非推奨。代わりに autoUpdateMode を使用してください。
     */
    appAutoUpdatePolicy?: string | null;
    /**
     * アプリに適用されるポリシー。
     */
    applications?: AMAPI_ApplicationPolicyExtend[];
    /**
     * 会社所有のデバイスで自動日付、時刻、およびタイムゾーンが有効になっているかどうか。これが設定されている場合、autoTimeRequired は無視されます。
     */
    autoDateAndTimeZone?: string | null;
    /**
     * ユーザーが日付と時刻を手動で設定できないようにする自動時刻が必要かどうか。 autoDateAndTimeZone が設定されている場合、このフィールドは無視されます。
     */
    autoTimeRequired?: boolean | null;
    /**
     * アプリケーションで構成されたもの以外のアプリケーションのインストールがブロックされているかどうか。
     * 設定すると、以前のポリシーの下でインストールされたが、ポリシーに表示されなくなったアプリケーションが自動的にアンインストールされます。
     */
    blockApplicationsEnabled?: boolean | null;
    /**
     * Bluetooth の構成を無効にするかどうか。
     */
    bluetoothConfigDisabled?: boolean | null;
    /**
     * Bluetooth 連絡先の共有を無効にするかどうか。
     */
    bluetoothContactSharingDisabled?: boolean | null;
    /**
     * Bluetooth を無効にするかどうか。bluetooth_config_disabled はユーザーがバイパスできるため、bluetooth_config_disabled よりもこの設定を優先してください。
     */
    bluetoothDisabled?: boolean | null;
    /**
     * カメラの使用と、ユーザーがカメラ アクセス トグルにアクセスできるかどうかを制御します。
     */
    cameraAccess?: string | null;
    /**
     * camera_access が CAMERA_ACCESS_UNSPECIFIED 以外の値に設定されている場合、これは効果がありません。
     * それ以外の場合、このフィールドはカメラを無効にするかどうかを制御します。true の場合、すべてのカメラが無効になり、それ以外の場合は使用可能になります。
     * 完全に管理されたデバイスの場合、このフィールドはデバイス上のすべてのアプリに適用されます。
     * 仕事用プロファイルの場合、このフィールドは仕事用プロファイル内のアプリにのみ適用され、仕事用プロファイル外のアプリのカメラ アクセスは影響を受けません。
     */
    cameraDisabled?: boolean | null;
    /**
     * セル ブロードキャストの構成を無効にするかどうか。
     */
    cellBroadcastsConfigDisabled?: boolean | null;
    /**
     * アプリの秘密鍵へのアクセスを決定するためのルール。詳細については、ChoosePrivateKeyRule を参照してください。
     */
    choosePrivateKeyRules?: AMAPI_ChoosePrivateKeyRule[];
    /**
     * デバイスがポリシーに準拠していない場合に実行する緩和アクションを宣言するルール。
     * 複数のルールの条件が満たされると、ルールの緩和アクションがすべて実行されます。
     * ルールの最大数は 100 です。代わりにポリシー適用ルールを使用してください。
     */
    complianceRules?: AMAPI_ComplianceRule[];
    /**
     * アプリ ウィンドウ以外のウィンドウの作成を無効にするかどうか。
     */
    createWindowsDisabled?: boolean | null;
    /**
     * ユーザー資格情報の構成を無効にするかどうか。
     */
    credentialsConfigDisabled?: boolean | null;
    /**
     * デバイスに適用されるクロスプロファイル ポリシー。
     */
    crossProfilePolicies?: AMAPI_CrossProfilePolicies;
    /**
     * ローミング データ サービスを無効にするかどうか。
     */
    dataRoamingDisabled?: boolean | null;
    /**
     * ユーザーがデバッグ機能を有効にすることを許可するかどうか。
     */
    debuggingFeaturesAllowed?: boolean | null;
    /**
     * 実行時のパーミッション リクエストのデフォルトのパーミッション ポリシー。
     */
    defaultPermissionPolicy?: string | null;
    /**
     * ロック画面に表示されるデバイス所有者情報。
     */
    deviceOwnerLockScreenInfo?: AMAPI_UserFacingMessage;
    /**
     * 暗号化が有効かどうか
     */
    encryptionPolicy?: string | null;
    /**
     * アプリの検証を強制的に有効にするかどうか。
     */
    ensureVerifyAppsEnabled?: boolean | null;
    /**
     * 設定からの出荷時設定へのリセットを無効にするかどうか。
     */
    factoryResetDisabled?: boolean | null;
    /**
     * 工場出荷時設定へのリセット保護のためのデバイス管理者の電子メール アドレス。
     * デバイスが出荷時設定にリセットされると、デバイスのロックを解除するために、これらの管理者の 1 人が Google アカウントのメール アドレスとパスワードでログインする必要があります。
     * 管理者が指定されていない場合、デバイスは出荷時設定へのリセット保護を提供しません。
     */
    frpAdminEmails?: string[] | null;
    /**
     * ユーザーが楽しむことができるかどうか。設定でイースターエッグ ゲームを無効にするかどうかを制御します。
     */
    funDisabled?: boolean | null;
    /**
     * ユーザーによるアプリのインストールを無効にするかどうか。
     */
    installAppsDisabled?: boolean | null;
    /**
     * このフィールドは無効です。
     */
    installUnknownSourcesAllowed?: boolean | null;
    /**
     * true の場合、これはプライマリおよび/またはセカンダリ ディスプレイのロック画面を無効にします。(https://source.android.com/docs/core/display/multi_display/lock-screen)
     */
    keyguardDisabled?: boolean | null;
    /**
     * ウィジェットなどのキーガードのカスタマイズを無効にしました。
     */
    keyguardDisabledFeatures?: string[] | null;
    /**
     * キオスク モードでのデバイスの動作を制御する設定。キオスク モードを有効にするには、kioskCustomLauncherEnabled を true に設定するか、ポリシーで installType KIOSK を使用してアプリを指定します。
     */
    kioskCustomization?: AMAPI_KioskCustomization;
    /**
     * キオスクのカスタム ランチャーを有効にするかどうか。
     * これにより、ホーム画面が、アプリケーション設定を介してインストールされたアプリにデバイスをロックダウンするランチャーに置き換えられます。
     * アプリは 1 つのページにアルファベット順に表示されます。
     * kioskCustomization を使用して、キオスク デバイスの動作をさらに構成します。
     */
    kioskCustomLauncherEnabled?: boolean | null;
    /**
     * 有効化された位置検出の程度。
     */
    locationMode?: string | null;
    /**
     * デバイス管理者の設定画面でユーザーに表示されるメッセージ。
     */
    longSupportMessage?: AMAPI_UserFacingMessage;
    /**
     * デバイスがロックされるまでのユーザー アクティビティの最大時間 (ミリ秒)。値 0 は、制限がないことを意味します。
     */
    maximumTimeToLock?: string | null;
    /**
     * マイクの使用と、ユーザーがマイク アクセス トグルにアクセスできるかどうかを制御します。これは、完全に管理されたデバイスにのみ適用されます。
     */
    microphoneAccess?: string | null;
    /**
     * 許可される最小の Android API レベル。
     */
    minimumApiLevel?: number | null;
    /**
     * モバイル ネットワークの構成が無効になっているかどうか。
     */
    mobileNetworksConfigDisabled?: boolean | null;
    /**
     * アカウントの追加または削除が無効になっているかどうか。
     */
    modifyAccountsDisabled?: boolean | null;
    /**
     * 物理外部メディアをマウントするユーザーを無効にするかどうか。
     */
    mountPhysicalMediaDisabled?: boolean | null;
    /**
     * ポリシーの名前
     */
    name?: string | null;
    /**
     * ネットワーク エスケープ ハッチを有効にするかどうか。
     * 起動時にネットワーク接続を確立できない場合、エスケープ ハッチは、デバイス ポリシーを更新するために一時的にネットワークに接続するようにユーザーに促します。
     * ポリシーを適用すると、一時的なネットワークは削除され、デバイスは引き続き起動します。
     * これにより、最後のポリシーに適切なネットワークがなく、デバイスがロック タスク モードでアプリを起動する場合、またはユーザーがデバイス設定にアクセスできない場合に、ネットワークに接続できなくなります。
     * 注: wifiConfigDisabled を true に設定すると、特定の状況下でこの設定が上書きされます。詳細については、wifiConfigDisabled を参照してください。
     */
    networkEscapeHatchEnabled?: boolean | null;
    /**
     * ネットワーク設定のリセットを無効にするかどうか。
     */
    networkResetDisabled?: boolean | null;
    /**
     * この機能は一般には利用できません。
     */
    //oncCertificateProviders?: AMAPI_OncCertificateProvider[];
    /**
     * デバイスのネットワーク構成。詳細については、ネットワークの構成を参照してください。
     */
    openNetworkConfiguration?: {
        [key: string]: any;
    } | null;
    /**
     * NFC を使用してアプリからデータを送信することを無効にするかどうか。
     */
    outgoingBeamDisabled?: boolean | null;
    /**
     * 発信通話を無効にするかどうか。
     */
    outgoingCallsDisabled?: boolean | null;
    /**
     * パスワード要件ポリシー。ポリシーで password_scope フィールドを設定することにより、仕事用プロファイルまたは完全に管理されたデバイスに対して異なるポリシーを設定できます。
     */
    passwordPolicies?: AMAPI_PasswordRequirements[];
    /**
     * パスワード要件。
     * フィールド password_requirements.require_password_unlock は設定しないでください。
     * 非推奨 - passwordPolicies を使用します。
     * 注: PasswordQuality の複雑さに基づく値、つまり COMPLEXITY_LOW、COMPLEXITY_MEDIUM、および COMPLEXITY_HIGH は、ここでは使用できません。
     * ここでは、unified_lock_settings を使用できません。
     */
    passwordRequirements?: AMAPI_PasswordRequirements;
    /**
     * すべてのアプリに対する明示的な許可またはグループの付与または拒否。これらの値は、default_permission_policy をオーバーライドします。
     */
    permissionGrants?: AMAPI_PermissionGrant[];
    /**
     * 許可されたアクセシビリティ サービスを指定します。フィールドが設定されていない場合は、任意のアクセシビリティ サービスを使用できます。
     * このフィールドが設定されている場合、このリストのアクセシビリティ サービスとシステムの組み込みアクセシビリティ サービスのみを使用できます。
     * 特に、フィールドが空に設定されている場合、システムの組み込みアクセシビリティ サービスのみを使用できます。
     * 
     * これを仕事用プロファイルに適用すると、個人用プロファイルと仕事用プロファイルの両方に影響します。
     */
    permittedAccessibilityServices?: AMAPI_PackageNameList;
    /**
     * 存在する場合、このリストのパッケージによって提供される入力メソッドのみが許可されます。 
     * このフィールドが存在するがリストが空の場合、システム入力メソッドのみが許可されます。
     */
    permittedInputMethods?: AMAPI_PackageNameList;
    /**
     * デフォルトのインテント ハンドラー アクティビティ。
     */
    persistentPreferredActivities?: AMAPI_PersistentPreferredActivity[];
    /**
     * 会社所有のデバイスでの個人的な使用を管理するポリシー。
     */
    personalUsagePolicies?: AMAPI_PersonalUsagePolicies;
    /**
     * このモードは、Play ストアでユーザーが使用できるアプリと、アプリがポリシーから削除されたときのデバイス上の動作を制御します。
     */
    playStoreMode?: string | null;
    /**
     * 特定のポリシーをデバイスに適用できない場合の動作を定義するルール
     */
    policyEnforcementRules?: AMAPI_PolicyEnforcementRule[];
    /**
     * 仕事用プロファイルで優先ネットワーク サービスを有効にするかどうかを制御します。
     * ex, 
     * 組織は、従業員のデバイスからのすべての仕事用データが企業専用のネットワーク サービスを介して送信されるという契約を通信事業者と結んでいる場合があります。
     * サポートされている優先ネットワーク サービスの例は、5G ネットワーク上のエンタープライズ スライスです。
     * これは、完全に管理されたデバイスには影響しません。
     */
    preferentialNetworkService?: string | null;
    /**
     * ChoosePrivateKeyRules に一致するルールがない場合に、ユーザーが秘密鍵のエイリアスを選択できるように、デバイスに UI を表示できるようにします。
     * Android P より前のデバイスの場合、これを設定すると、エンタープライズ キーが脆弱なままになる可能性があります。
     */
    privateKeySelectionEnabled?: boolean | null;
    /**
     * ネットワークに依存しないグローバル HTTP プロキシ。通常、プロキシは open_network_configuration でネットワークごとに構成する必要があります。
     * ただし、一般的な内部フィルタリングなどの特殊な構成では、グローバル HTTP プロキシが役立つ場合があります。 
     * プロキシにアクセスできない場合、ネットワーク アクセスが切断される可能性があります。
     * グローバル プロキシは単なる推奨事項であり、一部のアプリでは無視される場合があります。
     */
    recommendedGlobalProxy?: AMAPI_ProxyInfo;
    /**
     * 他のユーザーの削除を無効にするかどうか。
     */
    removeUserDisabled?: boolean | null;
    /**
     * デバイスのセーフ ブートへの再起動を無効にするかどうか。
     */
    safeBootDisabled?: boolean | null;
    /**
     * 画面キャプチャを無効にするかどうか。
     */
    screenCaptureDisabled?: boolean | null;
    /**
     * セットアップ プロセス中に実行するアクション。最大で 1 つのアクションを指定できます。
     */
    setupActions?: AMAPI_SetupAction[];
    /**
     * ユーザー アイコンの変更を無効にするかどうか。
     */
    setUserIconDisabled?: boolean | null;
    /**
     * 壁紙の変更を無効にするかどうか。
     */
    setWallpaperDisabled?: boolean | null;
    /**
     * 現在地の共有を無効にするかどうか。share_location_disabled は、完全に管理されたデバイスと個人所有の仕事用プロファイルの両方でサポートされています。
     */
    shareLocationDisabled?: boolean | null;
    /**
     * 管理者によって機能が無効にされている場合に、設定画面でユーザーに表示されるメッセージ。メッセージが 200 文字を超える場合、切り捨てられることがあります。
     */
    shortSupportMessage?: AMAPI_UserFacingMessage;
    /**
     * 最初の使用時にヒントをスキップするフラグ。エンタープライズ管理者は、アプリのシステム推奨事項を有効にして、最初の起動時にユーザー チュートリアルやその他の導入ヒントをスキップできます。
     */
    skipFirstUseHintsEnabled?: boolean | null;
    /**
     * SMS メッセージの送受信を無効にするかどうか。
     */
    smsDisabled?: boolean | null;
    /**
     * ステータス バーを無効にするかどうか。これにより、全画面モードからの脱出を可能にする通知、クイック設定、およびその他の画面オーバーレイが無効になります。
     * 非推奨。キオスク デバイスのステータス バーを無効にするには、InstallType KIOSK または kioskCustomLauncherEnabled を使用します。
     */
    statusBarDisabled?: boolean | null;
    /**
     * ステータス レポートの設定
     */
    statusReportingSettings?: AMAPI_StatusReportingSettings;
    /**
     * デバイスがオンのままになるモードで接続されたバッテリー。
     * この設定を使用する場合は、maximum_time_to_lock をクリアして、デバイスがオンのままでロックされないようにすることをお勧めします。
     */
    stayOnPluggedModes?: string[] | null;
    /**
     * OS アップデートの適用方法を制御するシステム アップデート ポリシー。
     * 更新の種類が WINDOWED の場合、更新ウィンドウは Play アプリの更新にも自動的に適用されます。
     */
    systemUpdate?: AMAPI_SystemUpdate;
    /**
     * テザリングとポータブル アクセス ポイントの構成を無効にするかどうか。
     */
    tetheringConfigDisabled?: boolean | null;
    /**
     * ユーザーによるアプリケーションのアンインストールを無効にするかどうか。
     * これにより、アプリケーションを使用して削除されたものであっても、アプリがアンインストールされなくなります
     */
    uninstallAppsDisabled?: boolean | null;
    /**
     * Microphone_access が MICROPHONE_ACCESS_UNSPECIFIED 以外の値に設定されている場合、これは効果がありません。
     * それ以外の場合、このフィールドはマイクを無効にするかどうかを制御します。true の場合、すべてのマイクが無効になり、それ以外の場合は使用可能になります。
     * これは、完全に管理されたデバイスでのみ使用できます。
     */
    unmuteMicrophoneDisabled?: boolean | null;
    /**
     * デバイス アクティビティ ログの構成。
     */
    usageLog?: AMAPI_UsageLog;
    /**
     * USB 経由でのファイル転送を無効にするかどうか。
     * これは、会社所有のデバイスでのみサポートされています。
     */
    usbFileTransferDisabled?: boolean | null;
    /**
     * USB ストレージが有効かどうか。非推奨。
     */
    usbMassStorageEnabled?: boolean | null;
    /**
     * ポリシーのバージョン。これは読み取り専用フィールドです。ポリシーが更新されるたびに、バージョンが増分されます。
     */
    version?: string | null;
    /**
     * VPN の構成を無効にするかどうか。
     */
    vpnConfigDisabled?: boolean | null;
    /**
     * Wi-Fi アクセス ポイントの構成を無効にするかどうか。
     * 注: 起動時にネットワーク接続を確立できず、Wi-Fi の構成が無効になっている場合、デバイス ポリシーを更新するためにネットワーク エスケープ ハッチが表示されます。
     * (networkEscapeHatchEnabled を参照してください).
     */
    wifiConfigDisabled?: boolean | null;
    /**
     * 非推奨 - wifi_config_disabled を使用します。
     */
    wifiConfigsLockdownEnabled?: boolean | null;
    /**
     * Wi-Fi、USB データ アクセス、キーボード/マウスの接続などのデバイス接続のコントロールについて説明します。
     */
    deviceConnectivityManagement?: AMAPI_DeviceConnectivityManagement | null;
}
/**
 * デバイスまたは仕事用プロファイルが settingName で指定されたポリシーに準拠していない場合に実行するアクションを定義するルール。
 */
export class AMAPI_PolicyEnforcementRule {
    /**
     * 会社所有のデバイスまたは仕事用プロファイル内のアプリとデータへのアクセスをブロックするアクション。
     * このアクションは、コンプライアンスの問題を修正する方法に関する情報 (可能な場合) を含むユーザー向けの通知もトリガーします。
     * 注:wipeAction も指定する必要があります。
     */
    blockAction?: AMAPI_BlockAction;
    /**
     * 適用する最上位のポリシー。たとえば、applications または passwordPolicies です。
     */
    settingName?: string | null;
    /**
     * 会社所有のデバイスをリセットするか、仕事用プロファイルを削除するアクション。注: blockAction も指定する必要があります。
     */
    wipeAction?: AMAPI_WipeAction;
}
/**
 * デバイスのセキュリティ体制に関する追加の詳細。
 */
export class AMAPI_PostureDetail {
    /**
     * このセキュリティ リスクを軽減し、デバイスのセキュリティ体制を改善するための、対応する管理者向けのアドバイス。
     */
    advice?: AMAPI_UserFacingMessage[];
    /**
     * デバイスのセキュリティ体制に悪影響を及ぼす特定のセキュリティ リスク。
     */
    securityRisk?: string | null;
}
/**
 * 電源管理イベント。
 */
export class AMAPI_PowerManagementEvent {
    /**
     * BATTERY_LEVEL_COLLECTED イベントの場合、バッテリー レベル (パーセンテージ)。
     */
    batteryLevel?: number | null;
    /**
     * イベントの作成時間。
     */
    createTime?: string | null;
    /**
     * イベントタイプ。
     */
    eventType?: string | null;
}
/**
 * HTTP プロキシの構成情報。ダイレクト プロキシの場合は、host、port、および exclude_hosts フィールドを設定します。
 * PAC スクリプト プロキシの場合は、pac_uri フィールドを設定します。
 */
export class AMAPI_ProxyInfo {
    /**
     * ダイレクト プロキシの場合、プロキシがバイパスされるホスト。ホスト名には、*.example.com などのワイルドカードを含めることができます。
     */
    excludedHosts?: string[] | null;
    /**
     * ダイレクト プロキシのホスト。
     */
    host?: string | null;
    /**
     * プロキシの構成に使用される PAC スクリプトの URI。
     */
    pacUri?: string | null;
    /**
     * ダイレクト プロキシのポート。
     */
    port?: number | null;
}
/**
 * 現在のデバイスの状態と適用されているポリシーによって決定される、デバイスのセキュリティ体制。
 */
export class AMAPI_SecurityPosture {
    /**
     * デバイスのセキュリティ体制の値。
     */
    devicePosture?: string | null;
    /**
     * デバイスのセキュリティ体制に関する追加の詳細。
     */
    postureDetails?: AMAPI_PostureDetail[];
}
/**
 * セットアップ中に実行されるアクション。
 */
export class AMAPI_SetupAction {
    /**
     * このアクションの説明。
     */
    description?: AMAPI_UserFacingMessage;
    /**
     * アプリを起動するアクション。
     * アプリは、キー com.google.android.apps.work.clouddpc を持つエクストラを含むインテントで起動されます。
     * EXTRA_LAUNCHED_AS_SETUP_ACTION ブール値 true に設定して、これが設定アクション フローであることを示します。
     */
    launchApp?: AMAPI_LaunchAppAction;
    /**
     * このアクションのタイトル。
     */
    title?: AMAPI_UserFacingMessage;
}
/**
 * 企業のサインインの詳細を含むリソース。
 */
export class AMAPI_SigninDetail {
    /**
     * この登録トークンでプロビジョニングされたデバイスで個人的な使用を許可するかどうかを制御します。
     * 会社所有端末の場合: 個人使用を有効にすると、ユーザーはデバイスで仕事用プロファイルを設定できます。
     *                     個人使用を無効にするには、ユーザーが完全に管理されたデバイスとしてデバイスをプロビジョニングする必要があります。
     * 個人所有のデバイスの場合: 個人使用を有効にすると、ユーザーはデバイスで仕事用プロファイルを設定できます。
     *                     個人使用を無効にすると、デバイスがプロビジョニングされなくなります。個人所有のデバイスで個人使用を無効にすることはできません。
     */
    allowPersonalUsage?: string | null;
    /**
     * この登録トークンでデバイスを登録するための QR コードを生成するために使用できる UTF-8 表現を持つ JSON 文字列。
     * NFC を使用してデバイスを登録するには、NFC レコードに JSON のプロパティのシリアル化された java.util.Properties 表現が含まれている必要があります。
     * これは、サーバーによって生成される読み取り専用フィールドです。
     */
    qrCode?: string | null;
    /**
     * カスタム サインイン フローをトリガーするために使用される企業全体の登録トークン。これは、サーバーによって生成される読み取り専用フィールドです。
     */
    signinEnrollmentToken?: string | null;
    /**
     * デバイスがサインイン登録トークンでプロビジョニングされている場合の認証用のサインイン URL。
     * サインイン エンドポイントは、ログインを成功させるために https://enterprise.google.com/android/enroll?et= の形式の URL で認証フローを終了する必要があります。またはログインに失敗した場合は https://enterprise.google.com/android/enroll/invalid 。
     */
    signinUrl?: string | null;
}
/**
 * エンタープライズ サインアップ URL。
 */
export class AMAPI_SignupUrl {
    /**
     * リソースの名前。enterprise.create を呼び出してエンタープライズ サインアップ フローを完了するときに、signupUrl フィールドでこの値を使用します。
     */
    name?: string | null;
    /**
     * エンタープライズ管理者がエンタープライズを登録できる URL。ページを iframe に表示できません。
     */
    url?: string | null;
}
/**
 * デバイス ソフトウェアに関する情報。
 */
export class AMAPI_SoftwareInfo {
    /**
     * ユーザーに表示するための Android ビルド ID 文字列。ex, shamu-userdebug 6.0.1 MOB30I 2756745 dev-keys.
     */
    androidBuildNumber?: string | null;
    /**
     * ビルド時間。
     */
    androidBuildTime?: string | null;
    /**
     * Android Device Policy アプリのバージョン コード。
     */
    androidDevicePolicyVersionCode?: number | null;
    /**
     * ユーザーに表示される Android Device Policy アプリのバージョン。
     */
    androidDevicePolicyVersionName?: string | null;
    /**
     * ユーザーに表示される Android バージョン文字列。ex, 6.0.1.
     */
    androidVersion?: string | null;
    /**
     * システム ブートローダーのバージョン番号, ex. 0.6.7.
     */
    bootloaderVersion?: string | null;
    /**
     * システム パッケージに関連付けられた android.content.pm.Signature の SHA-256 ハッシュ。これを使用して、システム ビルドが変更されていないことを確認できます。
     *  (https://developer.android.com/reference/android/content/pm/Signature.html)
     */
    deviceBuildSignature?: string | null;
    /**
     * カーネルのバージョン, ex, 2.6.32.9-g103d848.
     */
    deviceKernelVersion?: string | null;
    /**
     * デバイスのプライマリ ロケールの IETF BCP 47 言語コード。
     */
    primaryLanguageCode?: string | null;
    /**
     * セキュリティパッチレベル, ex. 2016-05-01.
     */
    securityPatchLevel?: string | null;
    /**
     * 潜在的な保留中のシステム アップデートに関する情報。
     */
    systemUpdateInfo?: AMAPI_SystemUpdateInfo;
}
/**
 * ステータス レポートの動作を制御する設定。
 */
export class AMAPI_StatusReportingSettings {
    /**
     * アプリケーション レポートの設定。application_reports_enabled が true の場合にのみ適用されます。
     */
    applicationReportingSettings?: AMAPI_ApplicationReportingSettings;
    /**
     * アプリ レポートが有効かどうか。
     */
    applicationReportsEnabled = true;
    /**
     * コモン クライテリア モードのレポートが有効かどうか。
     */
    commonCriteriaModeEnabled?: boolean | null;
    /**
     * デバイス設定のレポートが有効かどうか。
     */
    deviceSettingsEnabled?: boolean | null;
    /**
     * ディスプレイ レポートを有効にするかどうか。レポート データは、仕事用プロファイルを持つ個人所有のデバイスでは利用できません。
     */
    displayInfoEnabled?: boolean | null;
    /**
     * ハードウェア ステータス レポートが有効かどうか。レポート データは、仕事用プロファイルを持つ個人所有のデバイスでは利用できません。
     */
    hardwareStatusEnabled?: boolean | null;
    /**
     * メモリ イベント レポートを有効にするかどうか。
     */
    memoryInfoEnabled?: boolean | null;
    /**
     * ネットワーク情報レポートが有効かどうか。
     */
    networkInfoEnabled?: boolean | null;
    /**
     * 電源管理イベント レポートが有効かどうか。 レポート データは、仕事用プロファイルを持つ個人所有のデバイスでは利用できません。
     */
    powerManagementEventsEnabled?: boolean | null;
    /**
     * ソフトウェア情報レポートが有効かどうか。
     */
    softwareInfoEnabled?: boolean | null;
    /**
     * システム プロパティのレポートを有効にするかどうか。
     */
    systemPropertiesEnabled?: boolean | null;
}
/**
 * システム更新を管理するための構成
 */
export class AMAPI_SystemUpdate {
    /**
     * タイプが WINDOWED の場合、デバイスの現地時間で午前 0 時からの分数として測定されるメンテナンス ウィンドウの終了。
     * この値は、0 から 1439 までの範囲である必要があります。
     * この値が start_minutes 未満の場合、メンテナンス ウィンドウは午前 0 時まで続きます。
     * 指定されたメンテナンス ウィンドウが 30 分未満の場合、実際のウィンドウは開始時刻を 30 分超えて延長されます。
     */
    endMinutes?: number | null;
    /**
     * デバイスで実行されている OS バージョンをフリーズするために無線 (OTA) システム更新が延期される、毎年繰り返される期間。
     * デバイスが無期限にフリーズしないようにするには、各フリーズ期間を 60 日以上空ける必要があります。
     */
    freezePeriods?: AMAPI_FreezePeriod[];
    /**
     * タイプが WINDOWED の場合、メンテナンス ウィンドウの開始。デバイスの現地時間で午前 0 時からの分数として測定されます。
     * この値は、0 から 1439 までの範囲である必要があります。
     */
    startMinutes?: number | null;
    /**
     * 設定するシステム アップデートのタイプ。
     */
    type?: string | null;
}
/**
 * 潜在的な保留中のシステム アップデートに関する情報。
 */
export class AMAPI_SystemUpdateInfo {
    /**
     * 更新が最初に利用可能になった時刻。ゼロ値は、このフィールドが設定されていないことを示します。
     * このフィールドは、更新が利用可能な場合 (つまり、updateStatus が UPDATE_STATUS_UNKNOWN でも UP_TO_DATE でもない場合) にのみ設定されます。
     */
    updateReceivedTime?: string | null;
    /**
     * 更新のステータス: 更新が存在するかどうか、および更新の種類。
     */
    updateStatus?: string | null;
}
/**
 * デバイス上の特定の SIM カードに関連付けられているテレフォニー情報。Android API レベル 23 以降のフル マネージド デバイスでのみサポートされます。
 */
export class AMAPI_TelephonyInfo {
    /**
     * この SIM カードに関連付けられているキャリア名。
     */
    carrierName?: string | null;
    /**
     * この SIM カードに関連付けられている電話番号。
     */
    phoneNumber?: string | null;
}
/**
 * プロビジョニング中に同意する利用規約ページ。
 */
export class AMAPI_TermsAndConditions {
    /**
     * 適切にフォーマットされた HTML 文字列。android.text.Html#fromHtml を使用してクライアントで解析されます。
     */
    content?: AMAPI_UserFacingMessage;
    /**
     * HTML コンテンツの上に表示される短いヘッダー。
     */
    header?: AMAPI_UserFacingMessage;
}
/**
 * デバイスから収集され、Pub/Sub 通知を介して報告されるデバイス アクティビティ ログのタイプを制御します。 (https://developers.google.com/android/management/notifications).
 */
export class AMAPI_UsageLog {
    /**
     * 有効にするログの種類を指定します。使用状況ログが有効になっている場合、ユーザーはオンデバイス メッセージを受け取ることに注意してください。
     */
    enabledLogTypes?: string[] | null;
    /**
     * モバイル データ経由でアップロードできる有効なログ タイプを指定します。デフォルトでは、デバイスが WiFi に接続すると、ログはアップロードのためにキューに入れられます。
     */
    uploadOnCellularAllowed?: string[] | null;
}
/**
 * 企業に所属するユーザー。
 */
export class AMAPI_User {
    /**
     * user342 や asset#44418 など、このユーザー用に作成した一意の識別子。
     * このフィールドは、ユーザーの作成時に設定する必要があり、更新できません。
     * このフィールドには、個人を特定できる情報 (PII) を含めることはできません。
     * この識別子は 1024 文字以下にする必要があります。そうしないと、ポリシーの更新リクエストは失敗します。
     */
    accountIdentifier?: string | null;
}
/**
 * ロケール情報を含むユーザー向けメッセージを提供します。メッセージの最大長は 4096 文字です。
 */
export class AMAPI_UserFacingMessage {
    /**
     * ローカライズされたメッセージが指定されていない場合、またはユーザーのロケールがローカライズされたメッセージのいずれとも一致しない場合に表示される既定のメッセージ。
     * ローカライズされたメッセージが提供されている場合は、デフォルトのメッセージを提供する必要があります。
     */
    defaultMessage?: string | null;
    /**
     * ロケールが整形式の BCP 47 言語コード (en-US、es-ES、fr など) であるペアを含むマップ。
     * (https://www.w3.org/International/articles/language-tags/) 
     */
    localizedMessages?: {
        [key: string]: string;
    } | null;
}
/**
 * ウェブアプリ。
 */
export class AMAPI_WebApp {
    /**
     * Web アプリの表示モード。
     */
    displayMode?: string | null;
    /**
     * Web アプリのアイコンのリスト。少なくとも 1 つの要素が必要です。
     */
    icons?: AMAPI_WebAppIcon[];
    /**
     *  サーバーによって生成される Web アプリの名前  enterprises/{enterpriseId\}/webApps/{packageName\}.
     */
    name?: string | null;
    /**
     * 開始 URL、つまり、ユーザーがアプリケーションを開いたときに読み込まれる URL。
     */
    startUrl?: string | null;
    /**
     * ユーザーに表示される Web アプリのタイトル(ex 他のアプリケーションのリストの中、またはアイコンのラベルとして).
     */
    title?: string | null;
    /**
     * アプリの現在のバージョン。
     * バージョンは、Web アプリの存続期間中に自動的に増加する可能性があることに注意してください。Google は内部でハウスキーピングを行って Web アプリを最新の状態に保ちます。
     */
    versionCode?: string | null;
}
/**
 * Web アプリのアイコン。サポートされている形式は、png、jpg、および webp です。
 */
export class AMAPI_WebAppIcon {
    /**
     * base64url でエンコードされた文字列内の画像の実際のバイト (RFC4648、セクション 5「URL およびファイル名セーフ アルファベットによる Base 64 エンコード」を参照)。
     * - 画像の種類は png または jpg です。
     * - 画像は正方形が理想的です。
     * - 画像のサイズは 512x512 が理想的です。
     */
    imageData?: string | null;
}
/**
 * 会社所有のデバイスをリセットするか、仕事用プロファイルを削除するアクション。注: blockAction も指定する必要があります。
 */
export class AMAPI_WipeAction {
    /**
     * W工場出荷時設定にリセットされた保護データがデバイスに保存されているかどうか。この設定は仕事用プロファイルには適用されません。
     */
    preserveFrp?: boolean | null;
    /**
     * デバイスまたは仕事用プロファイルがワイプされるまでの、ポリシーが準拠していない日数。wipeAfterDays は blockAfterDays より大きくなければなりません。
     */
    wipeAfterDays?: number | null;
}
/**
 * Wi-Fi、USB データ アクセス、キーボード/マウスの接続などのデバイス接続のコントロールについて説明します。
 * Covers controls for device connectivity such as Wi-Fi, USB data access, keyboard/mouse connections, and more.
 */
export class AMAPI_DeviceConnectivityManagement{
    // Official Reference
    // https://developers.google.com/android/management/reference/rest/v1/enterprises.policies#deviceconnectivitymanagement

    // Unused policies are commented out

    // Controls what files and/or data can be transferred via USB. Supported only on company-owned devices.
    // USB経由で転送できるファイルやデータを制御します。会社所有のデバイスでのみサポートされます。
    usbDataAccess?: string | null;

    // Controls Wi-Fi configuring privileges. Based on the option set, user will have either full or limited or no control in configuring Wi-Fi networks.
    // Wi-Fi 設定権限を制御します。オプション設定に応じて、ユーザーは Wi-Fi ネットワークの設定を完全制御、制限付き制御、または制御なしのいずれかで実行できます。
    configureWifi?: string | null;

    // Controls configuring and using Wi-Fi direct settings. Supported on company-owned devices running Android 13 and above.
    // Wi-Fi Direct 設定の構成と使用を管理します。Android 13 以降を搭載した会社所有デバイスでサポートされます。
    // wifiDirectSettings? : string | null;

    // Controls tethering settings. Based on the value set, the user is partially or fully disallowed from using different forms of tethering.
    // テザリング設定を制御します。設定された値に基づいて、ユーザーは様々な形式のテザリングを部分的または完全に使用できなくなります。
    // tetheringSettings?: string | null;

    // Restrictions on which Wi-Fi SSIDs the device can connect to. Note that this does not affect which networks can be configured on the device. Supported on company-owned devices running Android 13 and above.
    // デバイスが接続できるWi-Fi SSIDの制限。デバイスで設定できるネットワークには影響しません。Android 13以降を搭載した会社所有デバイスでサポートされます。
    // wifiSsidPolicy?: AMAPI_WifiSsidPolicy;

    // Optional. Wi-Fi roaming policy.
    // オプション。Wi-Fi ローミング ポリシー。
    // wifiRoamingPolicy?: AMAPI_WifiSsidPolicy;

    // Optional. Controls whether Bluetooth sharing is allowed.
    // オプション。Bluetooth 共有を許可するかどうかを制御します。
    // bluetoothSharing?: string | null;

    // Optional. Preferential network service configuration. Setting this field will override preferentialNetworkService. This can be set on both work profiles and fully managed devices on Android 13 and above. See 5G network slicing guide for more details.
    // 省略可能。優先ネットワークサービスの設定。このフィールドを設定すると、preferentialNetworkService がオーバーライドされます。これは、Android 13 以降の仕事用プロファイルと完全に管理されたデバイスの両方で設定できます。詳しくは、5G ネットワーク スライシング ガイドをご覧ください。
    // preferentialNetworkServiceSettings?: AMAPI_PreferentialNetworkServiceSettings;

    // Optional. Access Point Name (APN) policy. Configuration for Access Point Names (APNs) which may override any other APNs on the device. See OVERRIDE_APNS_ENABLED and overrideApns for details.
    // オプション。アクセスポイント名（APN）ポリシー。デバイス上の他のAPNを上書きする可能性のあるアクセスポイント名（APN）の設定です。詳細は、OVERRIDE_APNS_ENABLEDとoverrideApnsを参照してください。
    // apnPolicy?: AMAPI_ApnPolicy;
}
/* Copyright (c)2023 Sky Co., LTD. All rights reserved. */
